/**
 * HousekeepingBlock — solo la limpieza que hoy puede torcerse.
 *
 * Hoy NO es el módulo de Housekeeping. Por defecto se ven como mucho cuatro
 * tareas, y son las excepciones: la salida con llegada detrás, lo que espera
 * una llegada sin estar lista y lo que ha pedido un huésped. La limpieza
 * rutinaria vive en su módulo, a un toque de "Ver las N limpiezas".
 *
 * Dos orígenes, una sola lista: lo que nace del calendario y lo que nace de
 * una petición del huésped, que entra como trabajo estructurado y no como un
 * mensaje suelto (§7.2, §8.1).
 */
import React, { useState } from "react";
import {
  HOUSEKEEPING_FLOW, HOUSEKEEPING_LABEL, HOUSEKEEPING_TONE,
  HOUSEKEEPING_TYPE_LABEL, URGENCY_LABEL, STAFF,
} from "@/data/todayV2Demo";
import { housekeepingNeedingAttention } from "@/lib/todayV2Selectors";
import {
  TodayBlock, TodayCard, RowList, Row, Meta, StatusPill, QuickAction,
  EmptyState, ShowMore,
} from "@/pages/admin/pms/v2/V2Primitives";

const DONE = new Set(["READY", "INSPECTED"]);
const HK_STAFF = STAFF.filter((s) => s.area === "housekeeping");
const VISIBLE_BY_DEFAULT = 4;

export default function HousekeepingBlock({
  tasks, onAdvanceTask, onAssignTask, onCompleteRequest,
}) {
  const [expanded, setExpanded] = useState(false);
  const focus = housekeepingNeedingAttention(tasks).slice(0, VISIBLE_BY_DEFAULT);
  const visible = expanded ? tasks : focus;
  const hasMore = tasks.length > visible.length;

  return (
    <TodayBlock
      id="today-housekeeping"
      title="Limpieza"
      count={tasks.length}
      testId="today-housekeeping"
    >
      {visible.length === 0 ? (
        <EmptyState testId="today-housekeeping-empty">
          Ninguna limpieza necesita atención ahora.
        </EmptyState>
      ) : (
        <TodayCard padding="p-0">
          <RowList testId="today-housekeeping-list">
            {visible.map((task) => (
              <TaskRow
                key={task.task_id}
                task={task}
                onAdvanceTask={onAdvanceTask}
                onAssignTask={onAssignTask}
                onCompleteRequest={onCompleteRequest}
              />
            ))}
          </RowList>
        </TodayCard>
      )}

      {(hasMore || expanded) && (
        <ShowMore
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          moreLabel={`Ver las ${tasks.length} limpiezas`}
          testId="today-housekeeping-more"
        />
      )}
    </TodayBlock>
  );
}

function TaskRow({ task, onAdvanceTask, onAssignTask, onCompleteRequest }) {
  const done = DONE.has(task.status);
  const next = HOUSEKEEPING_FLOW[HOUSEKEEPING_FLOW.indexOf(task.status) + 1];

  return (
    <Row
      testId={`hk-task-${task.task_id}`}
      className={task.same_day ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-stone-900 dark:text-stone-100">
              {task.unit_id}
              <span className="text-stone-400"> · {task.unit?.name}</span>
            </span>
            <StatusPill tone={HOUSEKEEPING_TONE[task.status]} testId={`hk-status-${task.task_id}`}>
              {HOUSEKEEPING_LABEL[task.status]}
            </StatusPill>
            {task.same_day && (
              <StatusPill tone="red" dot={false} testId={`hk-sameday-${task.task_id}`}>
                Salida y llegada hoy
              </StatusPill>
            )}
          </div>

          <Meta items={[
            task.from_request
              ? `${HOUSEKEEPING_TYPE_LABEL.request}: ${task.from_request.label}`
              : HOUSEKEEPING_TYPE_LABEL[task.type],
            URGENCY_LABEL[task.priority],
            task.assignee || "Sin asignar",
            task.next_arrival && !task.same_day ? "Llega hoy" : null,
            task.issue_id,
          ]} className="mt-0.5" />
        </div>

        <div className="shrink-0 flex items-center gap-1.5">
          {task.advanceable ? (
            <>
              {!task.assignee_id && (
                <label className="inline-flex items-center">
                  <span className="sr-only">Asignar {task.unit_id}</span>
                  <select
                    className="dv-input text-xs py-1 px-2 min-h-[44px] max-w-[7.5rem]"
                    value=""
                    onChange={(e) => onAssignTask(task.unit_id, e.target.value)}
                    data-testid={`hk-assign-${task.task_id}`}
                  >
                    <option value="">Asignar…</option>
                    {HK_STAFF.map((s) => (
                      <option key={s.staff_id} value={s.staff_id}>{s.name}</option>
                    ))}
                  </select>
                </label>
              )}
              <QuickAction
                label={next
                  ? `Marcar ${HOUSEKEEPING_LABEL[next].toLowerCase()}`
                  : HOUSEKEEPING_LABEL[task.status]}
                primary={!done}
                disabled={!next}
                onClick={() => onAdvanceTask(task.unit_id)}
                testId={`hk-advance-${task.task_id}`}
              />
            </>
          ) : (
            <QuickAction
              label="Entregada"
              primary
              onClick={() => onCompleteRequest(task.from_request.request_id)}
              testId={`hk-deliver-${task.task_id}`}
            />
          )}
        </div>
      </div>
    </Row>
  );
}
